/**
 * Checkout protocol (Neel Slack DM 2026-10-19) — the writes behind deciding
 * a tenant's move-out date request, shared between the admin tenant
 * profile's Move-out tab and the colivIQ Brain card so the two surfaces
 * can never disagree about what "confirm" or "reject" actually does.
 *
 * Reject: send-admin-email, the same function BrainEmailComposer.tsx sends
 * through, then mark the row rejected — only once the email actually sent,
 * so a closed composer leaves the request pending rather than silently
 * rejecting it with nothing sent.
 */
import { supabase } from "@/lib/supabase";

/**
 * Approve a tenant's requested move-out date from the colivIQ Brain card.
 * Mirrors `TenantProfileDialog.tsx`'s `confirmMoveOutDate` + `approveMoveOutRequest`
 * write-for-write (same booking columns, same two lifecycle email invokes,
 * same request-row update) — kept as its own copy rather than extracting
 * that already-shipped, heavily-used function, since its audit-log/undo-banner
 * wiring is tied to that component's own state and not worth re-plumbing for
 * a second caller that doesn't need either.
 */
export async function approveMoveOutRequestFromBrain(args: {
  bookingId: number;
  requestId: string;
  requestedDate: string;
  /** `bookings.move_out_notice_at` — kept if already set, stamped now if not. */
  existingNoticeAt: string | null;
}): Promise<{
  /** False when someone else already decided this request before this call
   *  could claim it — nothing else in this function runs in that case, so
   *  the booking is untouched and no emails were sent. */
  requestRowUpdated: boolean;
}> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  // Claim the row BEFORE writing the booking or sending anything — a stale
  // Brain card confirmed a beat after the admin tab (or another Brain card
  // in a second session) must not overwrite an already-confirmed date or
  // re-send the lifecycle emails. Whoever's UPDATE actually flips a pending
  // row to approved is the only caller that proceeds past this point.
  const { data: claimedRows, error: claimError } = await supabase
    .from("move_out_date_requests")
    .update({
      status: "approved",
      decided_by: user?.id ?? null,
      decided_at: new Date().toISOString(),
    })
    .eq("id", args.requestId)
    .eq("status", "pending")
    .select("id");
  if (claimError) throw claimError;
  if (!claimedRows?.length) return { requestRowUpdated: false };

  const { error: bookingError } = await supabase
    .from("bookings")
    .update({
      status: "move_out_pending",
      move_out_date: args.requestedDate,
      move_out_notice_at: args.existingNoticeAt ?? new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq("id", args.bookingId);
  if (bookingError) {
    // The row is already claimed but the booking never actually moved —
    // put it back to pending rather than leave it reading "approved" with
    // nothing confirmed.
    await supabase
      .from("move_out_date_requests")
      .update({ status: "pending", decided_by: null, decided_at: null })
      .eq("id", args.requestId);
    throw bookingError;
  }

  // Fire-and-forget, same as the admin tab — the hourly ticks are the safety
  // net if either invoke fails.
  void supabase.functions
    .invoke("tenant-lifecycle-email-tick", {
      body: { bookingId: args.bookingId },
    })
    .catch(() => {});
  void supabase.functions
    .invoke("notify-owner-lifecycle", {
      body: { event: "move_out_date_confirmed", bookingId: args.bookingId },
    })
    .catch(() => {});

  return { requestRowUpdated: true };
}

export async function sendMoveOutRejectEmailAndMarkRejected(args: {
  requestId: string;
  toEmail: string;
  subject: string;
  body: string;
}): Promise<{
  /** False when no email was sent at all — the request was already
   *  decided (e.g. approved from the admin tab or a Brain card) by the
   *  time this call checked, before anything was sent. */
  emailSent: boolean;
  /** False when someone else already decided this request — either
   *  caught by the pending check below (emailSent is also false then)
   *  or, rarely, by the final status guard after the email had already
   *  gone out (emailSent is true in that case). */
  requestRowUpdated: boolean;
}> {
  // The composer can sit open for a while after it's drawn its draft — an
  // admin may approve this exact request (from the tab or a Brain card) in
  // that window. Re-read the status right before sending so a reject never
  // emails a tenant whose move-out was just confirmed; the final
  // status='pending' guard below still exists for the much smaller window
  // between this read and the send itself.
  const { data: current, error: readErr } = await supabase
    .from("move_out_date_requests")
    .select("status")
    .eq("id", args.requestId)
    .maybeSingle();
  if (readErr) throw readErr;
  if (current?.status !== "pending") {
    return { emailSent: false, requestRowUpdated: false };
  }

  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token ?? "";
  const { data, error } = await supabase.functions.invoke("send-admin-email", {
    body: {
      to: args.toEmail,
      subject: args.subject,
      text: args.body,
      sender: "kontakt",
    },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  if ((data as any)?.success === false)
    throw new Error((data as any).error || "Email not sent");

  const {
    data: { user },
  } = await supabase.auth.getUser();
  // `status = 'pending'` guard — mirrors the approve path. The email is
  // already sent by this point regardless (this function only ever reaches
  // here after a successful send), so a 0-row result just means someone
  // else decided this request in the meantime, not that the reject failed.
  const { data: rejectedRows, error: updErr } = await supabase
    .from("move_out_date_requests")
    .update({
      status: "rejected",
      decided_by: user?.id ?? null,
      decided_at: new Date().toISOString(),
    })
    .eq("id", args.requestId)
    .eq("status", "pending")
    .select("id");
  if (updErr) throw updErr;
  return { emailSent: true, requestRowUpdated: !!rejectedRows?.length };
}
